/**
 * AssetGalleryTab
 *
 * Gallery tab content: Recent generations for image selection
 *
 * @accessibility WCAG 2.1 AA compliant
 */

import { useState } from 'react';
import { Images, Loader2, Search, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSimpleGallery } from '@/hooks/useGalleryV2';
import { cn } from '@/lib/utils';
import { BrandContextSection } from './BrandContextSection';

interface AssetGalleryTabProps {
  onImageSelect: (file: File | null) => void;
  selectedBrandId: string | null;
  onBrandChange: (brandId: string | null) => void;
}

export function AssetGalleryTab({ onImageSelect, selectedBrandId, onBrandChange }: AssetGalleryTabProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(null);

  const { images: galleryImages, isLoading } = useSimpleGallery({
    page: 1,
    perPage: 50,
  });

  const handleImageClick = async (imageUrl: string, imageId: string) => {
    try {
      setSelectedImageUrl(imageUrl);

      // Fetch image and convert to File
      const response = await fetch(imageUrl);
      const blob = await response.blob();

      // Use image ID as filename
      const filename = `gallery-${imageId}.jpg`;

      const file = new File([blob], filename, { type: blob.type });
      onImageSelect(file);
    } catch (error) {
      console.error('Error loading gallery image:', error);
    }
  };

  const handleClear = () => {
    setSelectedImageUrl(null);
    onImageSelect(null);
  };

  // Filter images by search (ID, tags, prompt)
  const filteredImages = galleryImages.filter((img) => {
    if (!search) return true;

    const searchLower = search.toLowerCase();

    // Search in ID
    if (img.id.toLowerCase().includes(searchLower)) return true;

    // Search in tags (user tags)
    if (img.tags?.some(tag =>
      tag.name.toLowerCase().includes(searchLower)
    )) return true;

    // Search in prompt
    if (img.prompt.toLowerCase().includes(searchLower)) return true;

    // Search in display name
    if (img.display_name?.toLowerCase().includes(searchLower)) return true;

    return false;
  });

  return (
    <div className="flex flex-col h-full p-2 space-y-4">
      {/* Header */}
      <div>
        <p className="text-sm text-neutral-600 dark:text-neutral-400">
          {t('generation.selectImageFromGallery')}
        </p>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400 pointer-events-none" />
        <input
          type="text"
          placeholder={t('common.search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-3 py-2 text-sm border border-neutral-300 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-orange-500"
        />
      </div>

      {/* Selected Image Preview */}
      {selectedImageUrl && (
        <div className="flex justify-center">
          <div className="relative rounded-lg overflow-hidden bg-neutral-100 dark:bg-neutral-800">
            <img src={selectedImageUrl} alt="Selected" className="w-auto h-auto max-h-[200px] object-contain" />
            <button
              onClick={handleClear}
              className="absolute top-2 right-2 p-1.5 rounded-full bg-red-500 text-white hover:bg-red-600 transition-colors shadow-lg"
              aria-label={t('common.cancel')}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Image Grid */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
          </div>
        ) : filteredImages.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-neutral-500">
            <Images className="h-12 w-12 mb-2" />
            <p className="text-sm">
              {search ? t('common.noResults') : t('gallery.noImages')}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {filteredImages.map((image) => (
              <button
                key={image.id}
                onClick={() => handleImageClick(image.image_url || '', image.id)}
                className={cn(
                  'aspect-square rounded-lg overflow-hidden bg-neutral-100 dark:bg-neutral-800',
                  'transition-all hover:ring-2 hover:ring-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500',
                  selectedImageUrl === image.image_url && 'ring-2 ring-orange-500'
                )}
              >
                <img
                  src={image.image_url || ''}
                  alt={`Gallery image ${image.id}`}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Brand Context Section */}
      <BrandContextSection
        selectedBrandId={selectedBrandId}
        onBrandChange={onBrandChange}
        showHeader={false}
      />
    </div>
  );
}
